import React from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BookOpen, ShoppingCart, TrendingUp, Download, CheckCircle, Package, DollarSign, Globe, Clock, MessageSquare, Zap } from 'lucide-react';
import { toast } from 'sonner';

export const ProveedoresPage = () => {
  const handleDownload = () => {
    toast.success('Preparando descarga de VERSO Proveedores...');
  };

  const features = [
    {
      icon: ShoppingCart,
      title: 'Centralización de Solicitudes',
      description: 'Todas las solicitudes de cotización de talleres VERSO en un solo lugar, sin WhatsApp ni llamadas.'
    },
    {
      icon: DollarSign,
      title: 'Registro Rápido de Precios',
      description: 'Ingresa precios de repuestos directamente en la plataforma de forma ágil y eficiente.'
    },
    {
      icon: Clock,
      title: 'Ahorro de Tiempo Masivo',
      description: 'Elimina la necesidad de atender talleres por llamada o WhatsApp, centralizando todo en la web.'
    },
    {
      icon: MessageSquare,
      title: 'Envío de Promociones',
      description: 'Envía ofertas, descuentos y promociones especiales directamente a los talleres conectados.'
    },
    {
      icon: Zap,
      title: 'Recepción Eficiente',
      description: 'Optimiza el proceso de cotización con notificaciones instantáneas de nuevas solicitudes.'
    },
    {
      icon: Globe,
      title: 'Conexión con AUTOPRO',
      description: 'Integración directa con los talleres que usan VERSO Talleres para flujo automático.'
    }
  ];

  const benefits = [
    'Aumenta tus ventas conectándote con +15,000 talleres activos',
    'Reduce tiempo de cotización hasta un 70%',
    'Centraliza todas las solicitudes en una sola plataforma',
    'Envía promociones y descuentos masivamente',
    'Compite en tiempo real con otros proveedores',
    'Gestiona tu catálogo de repuestos digitalmente',
    'Recibe notificaciones de nuevas solicitudes',
    'Sin costos de llamadas ni mensajería'
  ];

  const stats = [
    { value: '+15k', label: 'Talleres Conectados' },
    { value: '70%', label: 'Menos Tiempo Cotizando' },
    { value: '2M+', label: 'Pedidos Procesados' },
    { value: '100%', label: 'Web-Based' }
  ];

  const howItWorks = [
    { step: '1', title: 'Recibe Solicitud', description: 'El taller envía solicitud de repuesto vía AUTOPRO' },
    { step: '2', title: 'Cotiza Rápido', description: 'Ingresas precio, marca y tiempo de entrega en la plataforma' },
    { step: '3', title: 'Taller Compara', description: 'El taller compara precios y elige la mejor opción' },
    { step: '4', title: 'Confirma Venta', description: 'Recibes confirmación automática del pedido' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat, index) => (
                  <Card key={index} className="bg-white/5 backdrop-blur-sm border-purple-500/20 p-6 text-center">
                    <div className="text-3xl font-bold text-purple-400 mb-2">{stat.value}</div>
                    <div className="text-gray-400 text-sm">{stat.label}</div>
                  </Card>
                ))}
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center space-x-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-4 py-2 mb-6">
                <Package className="text-purple-400" size={16} />
                <span className="text-purple-300 text-sm font-medium">VERSO PROVEEDORES</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Conecta con miles de
                <br />
                <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  talleres activos
                </span>
              </h1>
              <p className="text-gray-400 text-xl mb-8 leading-relaxed">
                Centraliza solicitudes de cotización, reduce tiempo de respuesta y aumenta tus ventas 
                con la plataforma que conecta proveedores con talleres en toda la región.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  onClick={handleDownload}
                  size="lg"
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg"
                >
                  <Download className="mr-2" size={20} />
                  Comenzar Ahora
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-purple-500 text-purple-400 hover:bg-purple-500/10 px-8 py-6 text-lg rounded-xl"
                >
                  Solicitar Demo
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">¿Cómo Funciona?</h2>
            <p className="text-gray-400 text-lg">Proceso simple y eficiente</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((item, index) => (
              <Card key={index} className="bg-white/5 backdrop-blur-sm border-purple-500/20 p-6 text-center hover:border-purple-500/40 transition-all">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-white">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Funcionalidades Principales</h2>
            <p className="text-gray-400 text-lg">Todo lo que necesitas para optimizar tus ventas</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="bg-white/5 backdrop-blur-sm border-purple-500/20 p-6 hover:border-purple-500/40 transition-all duration-300 group">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="text-purple-400" size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm">{feature.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="relative py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/30 backdrop-blur-sm p-12">
            <h2 className="text-4xl font-bold text-white mb-8 text-center">Beneficios de Unirte a VERSO</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="text-purple-400 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Card className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/30 backdrop-blur-sm p-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Únete a la Red de Proveedores Líder
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              Más de 2,000 proveedores ya confían en VERSO para gestionar sus cotizaciones
            </p>
            <Button 
              onClick={handleDownload}
              size="lg"
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-12 py-6 text-lg rounded-xl"
            >
              <Download className="mr-2" size={20} />
              Solicitar Acceso Gratis
            </Button>
          </Card>
        </div>
      </section>
    </div>
  );
};
